import { API_ENDPOINTS, httpClientInstance } from "@/api"
import type {
  BulkActionPayload,
  BulkActionResponse,
  Member,
  MemberFormData,
  MemberProfileFormData,
  PermissionType,
} from "@/types/member.types"

/**
 * Members Service
 *
 * Handles member management CRUD operations including:
 * - Fetching all members
 * - Creating new members
 * - Fetching single member details
 * - Updating existing members
 * - Deleting members
 * - Bulk actions (activate, deactivate, delete)
 */
export class MembersService {
  /**
   * Format API error response into user-friendly message
   * @param errorData Error data from API response
   */
  private formatErrorMessage(errorData: any): string {
    // If error data is a string, return it directly
    if (typeof errorData === "string") {
      return errorData
    }

    // If error has a detail field (common Django REST framework format)
    if (errorData?.detail) {
      return errorData.detail
    }

    // If error data is an object with field-specific errors
    if (typeof errorData === "object" && errorData !== null) {
      const errors: string[] = []

      // Iterate through each field and get the first error message
      for (const [field, messages] of Object.entries(errorData)) {
        if (Array.isArray(messages) && messages.length > 0) {
          // Format: "Field: Error message"
          const formattedField = field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
          errors.push(`${formattedField}: ${messages[0]}`)
        }
      }

      if (errors.length > 0) {
        return errors.join("\n")
      }
    }

    return "Operation failed. Please try again."
  }

  /**
   * Get all members, optionally scoped to a department or the unassigned pool
   * GET /members/accounts/list/?department=<id>|unassigned
   */
  async getAllMembers(params?: { department?: number | "unassigned" }): Promise<Member[]> {
    try {
      const response = await httpClientInstance.get<Member[]>(API_ENDPOINTS.members.accounts.list, params)
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Get a specific member by ID
   * GET /members/accounts/{id}/retrieve/
   * @param id Member ID
   */
  async getMemberById(id: number): Promise<Member> {
    try {
      const response = await httpClientInstance.get<Member>(API_ENDPOINTS.members.accounts.retrieve(id))
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Create a new member
   * POST /members/accounts/create/
   * @param formData Member form data
   */
  async createMember(formData: MemberFormData): Promise<Member> {
    try {
      const response = await httpClientInstance.post<Member>(API_ENDPOINTS.members.accounts.create, formData)
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Update an existing member
   * PUT /members/accounts/{id}/update/
   * @param id Member ID
   * @param formData Member form data
   */
  async updateMember(id: number, formData: Partial<MemberFormData>): Promise<Member> {
    try {
      // permission_types is read-only on the update endpoint — strip it from the payload
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { permission_types, ...updatePayload } = formData
      const response = await httpClientInstance.put<Member>(API_ENDPOINTS.members.accounts.update(id), updatePayload)
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Profile-only update (PATCH) that sends just the first and last name. This is what a
   * department admin uses: the backend rejects any other field from them.
   * PATCH /members/accounts/{id}/patch/
   */
  async updateMemberProfile(id: number, profile: MemberProfileFormData): Promise<Member> {
    try {
      return await httpClientInstance.patch<Member>(API_ENDPOINTS.members.accounts.patch(id), {
        first_name: profile.first_name,
        last_name: profile.last_name,
      })
    } catch (error: unknown) {
      const errorData = (error as { response?: { data?: unknown } }).response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      Object.assign(apiError, { fieldErrors: errorData })
      throw apiError
    }
  }

  /**
   * Reactivate a deactivated member. Super Admin only — must be a standalone
   * action, never bundled with a profile/permission edit (US-003).
   * PATCH /members/accounts/{id}/activate/
   */
  async activateMember(id: number): Promise<Member> {
    try {
      const response = await httpClientInstance.patch<{ member: Member }>(
        API_ENDPOINTS.members.accounts.activate(id)
      )
      return response.member
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Deactivate a member. Super Admin (any member) or a Department Admin (own
   * department only). Revokes sessions, clears department/cost_limit
   * (AC-015-05), and clears the department's admin if this member was it.
   * PATCH /members/accounts/{id}/deactivate/
   */
  async deactivateMember(id: number): Promise<Member> {
    try {
      const response = await httpClientInstance.patch<{ member: Member }>(
        API_ENDPOINTS.members.accounts.deactivate(id)
      )
      return response.member
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Assign permissions to a member
   * POST /members/accounts/{id}/assign_permissions/
   */
  async assignPermissions(id: number, permissionTypes: PermissionType[]): Promise<Member> {
    try {
      const response = await httpClientInstance.post<{ member: Member }>(
        API_ENDPOINTS.members.accounts.assignPermissions(id),
        { permission_types: permissionTypes }
      )
      return response.member
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Remove permissions from a member
   * POST /members/accounts/{id}/remove_permissions/
   */
  async removePermissions(id: number, permissionTypes: PermissionType[]): Promise<Member> {
    try {
      const response = await httpClientInstance.post<{ member: Member }>(
        API_ENDPOINTS.members.accounts.removePermissions(id),
        { permission_types: permissionTypes }
      )
      return response.member
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Assign this member to a department. 409 if already assigned elsewhere —
   * call removeMemberFromDepartment first to unassign them.
   * POST /members/accounts/{id}/assign-department/
   */
  async assignMemberToDepartment(id: number, departmentId: number): Promise<Member> {
    try {
      return await httpClientInstance.post<Member>(API_ENDPOINTS.members.accounts.assignDepartment(id), {
        department_id: departmentId,
      })
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Unassign this member from their current department. Safe to call even if
   * already unassigned.
   * POST /members/accounts/{id}/remove-department/
   */
  async removeMemberFromDepartment(id: number): Promise<Member> {
    try {
      return await httpClientInstance.post<Member>(API_ENDPOINTS.members.accounts.removeDepartment(id))
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Delete a member
   * DELETE /members/accounts/{id}/delete/
   * @param id Member ID
   */
  async deleteMember(id: number): Promise<void> {
    try {
      await httpClientInstance.delete<void>(API_ENDPOINTS.members.accounts.delete(id))
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Perform bulk action on multiple members
   * POST /members/accounts/bulk_action/
   * @param payload Bulk action payload
   */
  async bulkAction(payload: BulkActionPayload): Promise<BulkActionResponse> {
    try {
      const response = await httpClientInstance.post<BulkActionResponse>(
        API_ENDPOINTS.members.accounts.bulkAction,
        payload
      )
      return response
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      const apiError = new Error(message)
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }
}

/**
 * Singleton instance of MembersService
 */
export const membersService = new MembersService()

/**
 * Get all members, optionally scoped to a department or the unassigned pool
 */
export const getAllMembers = async (params?: { department?: number | "unassigned" }): Promise<Member[]> => {
  return membersService.getAllMembers(params)
}

/**
 * Assign a member to a department
 * @param id Member ID
 * @param departmentId Department ID
 */
export const assignMemberToDepartment = async (id: number, departmentId: number): Promise<Member> => {
  return membersService.assignMemberToDepartment(id, departmentId)
}

/**
 * Unassign a member from their current department
 * @param id Member ID
 */
export const removeMemberFromDepartment = async (id: number): Promise<Member> => {
  return membersService.removeMemberFromDepartment(id)
}

/**
 * Get a specific member by ID
 * @param id Member ID
 */
export const getMemberById = async (id: number): Promise<Member> => {
  return membersService.getMemberById(id)
}

/**
 * Create a new member
 * @param formData Member form data
 */
export const createMember = async (formData: MemberFormData): Promise<Member> => {
  return membersService.createMember(formData)
}

/**
 * Update an existing member
 * @param id Member ID
 * @param formData Member form data
 */
export const updateMember = async (id: number, formData: Partial<MemberFormData>): Promise<Member> => {
  return membersService.updateMember(id, formData)
}

/**
 * Delete a member
 * @param id Member ID
 */
export const deleteMember = async (id: number): Promise<void> => {
  return membersService.deleteMember(id)
}

/**
 * Reactivate a deactivated member
 * @param id Member ID
 */
export const activateMember = async (id: number): Promise<Member> => {
  return membersService.activateMember(id)
}

/**
 * Deactivate a member
 * @param id Member ID
 */
export const deactivateMember = async (id: number): Promise<Member> => {
  return membersService.deactivateMember(id)
}

/**
 * Perform bulk action on multiple members
 * @param payload Bulk action payload
 */
export const bulkAction = async (payload: BulkActionPayload): Promise<BulkActionResponse> => {
  return membersService.bulkAction(payload)
}
